"use client";

import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export type FotoDelVisor = {
  /** La que se ve en la pagina. */
  pequena: string;
  /** La misma foto en grande, que solo se pide al abrirla. */
  grande: string;
  autor: string | null;
};

/**
 * Las fotos de la ficha, y el visor para verlas de cerca.
 *
 * POR QUE ES DE CLIENTE Y EL RESTO DE LA FICHA NO: es lo unico de esta pagina
 * que reacciona a un clic. La rejilla se pinta igual en el servidor —quien
 * llegue con el javascript a medio cargar ve las fotos, no un hueco—, y el
 * visor solo aparece cuando alguien pulsa.
 *
 * LA GRANDE SOLO SE PIDE AL ABRIRLA. Cada tamaño distinto es una peticion
 * aparte a Google que se factura la primera vez, asi que las miniaturas se
 * quedan pequeñas y la grande no existe hasta que alguien la mira. El ancho de
 * la grande es el mismo que el de la portada, para que compartan la copia
 * guardada en vez de pagar dos.
 *
 * SE CIERRA COMO SE ESPERA: Escape, el aspa, o pulsando fuera de la foto. Y se
 * mueve con las flechas del teclado, que es lo que hace todo el mundo despues
 * de abrir la primera.
 */
export function VisorDeFotos({
  fotos,
  nombre,
  textos,
}: {
  fotos: FotoDelVisor[];
  nombre: string;
  textos: { cerrar: string; anterior: string; siguiente: string };
}) {
  const [abierta, setAbierta] = useState<number | null>(null);

  const cerrar = useCallback(() => setAbierta(null), []);
  const mover = useCallback(
    (paso: number) =>
      setAbierta((i) => (i == null ? i : (i + paso + fotos.length) % fotos.length)),
    [fotos.length],
  );

  // El teclado y el bloqueo del desplazamiento van juntos: mientras hay una
  // foto abierta, la pagina de detras no se mueve.
  useEffect(() => {
    if (abierta == null) return;
    const alPulsar = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") cerrar();
      if (evento.key === "ArrowRight") mover(1);
      if (evento.key === "ArrowLeft") mover(-1);
    };
    window.addEventListener("keydown", alPulsar);
    const desbordeAntes = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", alPulsar);
      document.body.style.overflow = desbordeAntes;
    };
  }, [abierta, cerrar, mover]);

  if (fotos.length === 0) return null;
  const [portada, ...resto] = fotos;

  return (
    <>
      <button
        type="button"
        onClick={() => setAbierta(0)}
        className="block w-full cursor-zoom-in"
        aria-label={nombre}
      >
        <img
          src={portada.pequena}
          alt={nombre}
          title={portada.autor || undefined}
          className="aspect-[16/6] w-full rounded-xl bg-zinc-50 object-cover"
        />
      </button>

      {resto.length > 0 && (
        <div className="mt-2 grid grid-cols-4 gap-2">
          {resto.map((foto, i) => (
            <button
              key={foto.pequena}
              type="button"
              onClick={() => setAbierta(i + 1)}
              className="cursor-zoom-in"
              aria-label={nombre}
            >
              <img
                src={foto.pequena}
                alt={nombre}
                title={foto.autor || undefined}
                loading="lazy"
                className="aspect-[4/3] w-full rounded-lg bg-zinc-50 object-cover transition-opacity hover:opacity-90"
              />
            </button>
          ))}
        </div>
      )}

      {abierta != null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={nombre}
          onClick={cerrar}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
        >
          <button
            type="button"
            onClick={cerrar}
            aria-label={textos.cerrar}
            className="absolute right-4 top-4 rounded-full p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X className="h-6 w-6" aria-hidden />
          </button>

          {fotos.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  mover(-1);
                }}
                aria-label={textos.anterior}
                className="absolute left-2 rounded-full p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white sm:left-6"
              >
                <ChevronLeft className="h-8 w-8" aria-hidden />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  mover(1);
                }}
                aria-label={textos.siguiente}
                className="absolute right-2 rounded-full p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white sm:right-6"
              >
                <ChevronRight className="h-8 w-8" aria-hidden />
              </button>
            </>
          )}

          <figure onClick={(e) => e.stopPropagation()} className="max-h-full">
            <img
              src={fotos[abierta].grande}
              alt={nombre}
              className="max-h-[85vh] w-auto max-w-full rounded-lg object-contain"
            />
            {fotos[abierta].autor && (
              <figcaption className="mt-2 text-center text-xs text-white/50">
                {fotos[abierta].autor}
              </figcaption>
            )}
          </figure>
        </div>
      )}
    </>
  );
}
